import config from "../config";
import { auth } from "../firebase";

/**
 * Safely constructs an API URL with the given path and query parameters.
 * Helps mitigate SonarQube security hotspots related to URL construction.
 */
export const createSafeUrl = (baseUrl, path, queryParams = {}) => {
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  if (cleanPath.includes("..")) {
    throw new Error("Invalid path traversal sequence detected");
  }

  let url;
  if (baseUrl) {
    const fullUrlString = baseUrl.endsWith("/")
      ? `${baseUrl}${cleanPath}`
      : `${baseUrl}/${cleanPath}`;
    url = new URL(fullUrlString);
  } else {
    url = new URL(`/${cleanPath}`, globalThis.location.origin);
  }

  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  }
  return url.toString();
};

/**
 * Validates that an ID is a safe string for URL paths.
 * Prevents path traversal and other injection attacks.
 */
export const sanitizeId = (id) => {
  if (typeof id !== "string") return "";
  // Allow only alphanumeric, hyphens, and underscores (common for Firebase IDs)
  return encodeURIComponent(id.replaceAll(/[^a-zA-Z0-9\-_]/g, ""));
};

export const formatApiErrorMessage = (errorData, fallbackMsg) => {
  if (!errorData) return fallbackMsg;
  if (typeof errorData === "string") return errorData;

  const detail = errorData.detail;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{"loc": ["body", "field_id"], "msg": "Input should be a valid string", "type": "string_type"}]
    const formattedErrors = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const locParts = Array.isArray(item.loc)
            ? item.loc.filter((segment) => segment !== "body")
            : [];
          const locPrefix =
            locParts.length > 0 ? `${locParts.join(".")}: ` : "";
          const msg = item.msg || item.message || JSON.stringify(item);
          return `${locPrefix}${msg}`;
        }
        return String(item);
      })
      .filter(Boolean);

    if (formattedErrors.length > 0) {
      return formattedErrors.join("; ");
    }
  } else if (detail && typeof detail === "object") {
    return detail.message || detail.msg || JSON.stringify(detail);
  }

  if (typeof errorData.message === "string") return errorData.message;
  if (typeof errorData.error === "string") return errorData.error;

  try {
    return JSON.stringify(errorData);
  } catch {
    return fallbackMsg;
  }
};

/**
 * A centralized wrapper around fetch that automatically handles Authentication
 * (Firebase Auth) and constructs the URL for our backend proxy.
 */
export const fetchData = async (path, options = {}, queryParams = {}) => {
  // Use our backend proxy
  const safeUrlString = createSafeUrl(config.apiBaseUrl, path, queryParams);
  const validatedUrl = new URL(safeUrlString);

  // Validate protocol against HTTP/HTTPS
  if (!["http:", "https:"].includes(validatedUrl.protocol)) {
    throw new Error("Invalid URL protocol");
  }

  // Ensure origin matches configured backend proxy or location origin
  if (config.apiBaseUrl?.startsWith("http")) {
    const expectedOrigin = new URL(config.apiBaseUrl).origin;
    if (validatedUrl.origin !== expectedOrigin) {
      throw new Error("Target URL origin mismatch");
    }
  }

  const headers = new Headers(options.headers || {});

  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      headers.set("Authorization", `Bearer ${token}`);
    } catch (e) {
      console.error("Failed to get Firebase token:", e);
    }
  } else if (process.env.NODE_ENV === "test") {
    headers.set("Authorization", `Bearer TEST_TOKEN`);
  }

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(validatedUrl.toString(), {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Attempt to extract the error payload
    let errorMsg = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMsg = formatApiErrorMessage(errorData, errorMsg);
    } catch (e) {
      console.warn("Could not parse error response body as JSON", e);
    }
    throw new Error(errorMsg);
  }

  // Databricks Lakebase API might return 204 No Content for inserts/deletes
  if (
    response.status === 204 ||
    response.headers?.get?.("content-length") === "0"
  ) {
    return null;
  }
  return response.json();
};

export const resolveDbUserId = async (user, firebaseUid) => {
  if (user.user_id) return user.user_id;

  if (typeof firebaseUid !== "string" || !Number.isNaN(Number(firebaseUid))) {
    return firebaseUid;
  }

  try {
    const users = await fetchData(
      `/users?firebase_id=eq.${encodeURIComponent(firebaseUid)}`,
    );
    if (users?.length > 0) {
      return users[0].user_id;
    }
    const parts = (user.name || user.displayName || "").split(" ");
    const createdUsers = await fetchData(`/users`, {
      method: "POST",
      headers: { Prefer: "return=representation" },
      body: JSON.stringify({
        firebase_id: firebaseUid,
        first_name: parts[0] || "User",
        last_name: parts.slice(1).join(" ") || "User",
        email: user.email || `${firebaseUid}@example.com`,
      }),
    });
    if (createdUsers?.length > 0) {
      return createdUsers[0].user_id;
    }
  } catch {
    // If /users query fails (e.g. unmocked in unit test), fallback to firebaseUid
  }
  return firebaseUid;
};

const createPlayerRecord = async (targetUserId, user) => {
  const parts = (user.name || user.displayName || "").split(" ");
  const playerResult = await fetchData(`/people`, {
    method: "POST",
    headers: { Prefer: "return=representation" },
    body: JSON.stringify({
      first_name: parts[0] || "User",
      last_name: parts.slice(1).join(" ") || "User",
      user_id: targetUserId,
    }),
  });

  if (playerResult?.length > 0) {
    return playerResult[0].person_id;
  }
  return null;
};

/**
 * Helper to get or create a DB user record and associated player record for a Firebase auth user.
 * Resolves Firebase string UIDs to integer user_id for PostgreSQL compatibility.
 */
export const getOrCreatePlayerForUser = async (user) => {
  if (!user) return null;
  const firebaseUid = user.uid || user.id || user.firebaseUid || "mock-user-id";
  if (!firebaseUid) return null;

  const targetUserId = await resolveDbUserId(user, firebaseUid);
  const existingPlayers = await fetchData(
    `/people?user_id=eq.${encodeURIComponent(targetUserId)}`,
  );
  if (existingPlayers?.length > 0) {
    return existingPlayers[0].person_id;
  }

  return createPlayerRecord(targetUserId, user);
};
